import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Receipt, Eye, XCircle, Banknote, User, Search,
  FileSearch, BadgeCheck, ArrowRight, Shield, Trash2, RotateCcw, Calendar, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ExpenseAnalytics from './ExpenseAnalytics';

// Status colors
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  pending_review: 'bg-blue-100 text-blue-800',
  pending_authorization: 'bg-purple-100 text-purple-800',
  pending_disbursement: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
  returned: 'bg-orange-100 text-orange-800',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  pending_authorization: 'Pending Authorization',
  pending_disbursement: 'Pending Approval',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  returned: 'Returned',
};

// Workflow Progress Component
const WorkflowProgress = ({ expense }) => {
  // New flow: 
  // Small amounts (<1M): Submitted → Review → Approval → Complete
  // Large amounts (≥1M): Submitted → Review → Approval → Authorization → Complete
  const stages = expense.requires_authorization
    ? ['Submitted', 'Review', 'Approval', 'Authorization']
    : ['Submitted', 'Review', 'Approval'];
  
  const currentStageMap = {
    'pending_review': 1,
    'pending_disbursement': 2,  // This is now "Approval" stage
    'pending_authorization': 3, // Only for high-value
    'completed': expense.requires_authorization ? 4 : 3,
    'rejected': -1,
    'returned': -2,
  };
  
  const currentIdx = currentStageMap[expense.status] || 0;
  
  if (expense.status === 'rejected') {
    return (
      <div className="flex items-center gap-1 text-xs text-red-600">
        <XCircle className="h-3 w-3" />
        <span>Rejected at {expense.rejected_at_stage || 'workflow'}</span>
      </div>
    );
  }
  
  if (expense.status === 'returned') {
    return (
      <div className="flex items-center gap-1 text-xs text-orange-600">
        <RotateCcw className="h-3 w-3" />
        <span>Returned for changes</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {stages.map((stage, idx) => (
        <React.Fragment key={stage}>
          <div className={`text-xs px-2 py-0.5 rounded ${
            idx < currentIdx ? 'bg-green-100 text-green-700' :
            idx === currentIdx ? 'bg-blue-100 text-blue-700 font-medium' :
            'bg-gray-100 text-gray-500'
          }`}>
            {stage}
          </div>
          {idx < stages.length - 1 && (
            <ArrowRight className={`h-3 w-3 ${idx < currentIdx ? 'text-green-500' : 'text-gray-300'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ExpenseManagement = () => {
  const { getAuthHeaders, currentOffice, user } = useAuth();
  const [activeTab, setActiveTab] = useState('approvals');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [userBadges, setUserBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [processing, setProcessing] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API_URL}/api/expenses/admin/all?${params}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses || []);
        setSummary(data.summary || {});
        setUserBadges(data.user_badges || []);
        const userRoles = user?.roles || [];
        setIsAdmin(userRoles.some(r => r.role === 'super_admin' || r.role === 'office_admin'));
      } else {
        toast.error('Failed to load expense requests');
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expense requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [statusFilter]);

  const openDetail = (expense) => {
    setSelectedExpense(expense);
    setShowDetailModal(true);
  };

  const openActionModal = (type, expense) => {
    setActionType(type);
    setSelectedExpense(expense);
    setActionNotes('');
    setPaymentRef('');
    setShowActionModal(true);
  };

  const handleAction = async () => {
    if (!selectedExpense) return;
    
    if (actionType === 'reject' && !actionNotes.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    if (actionType === 'send-back' && !actionNotes.trim()) {
      toast.error('Please provide a comment for sending back');
      return;
    }
    
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      let url = `${API_URL}/api/expenses/${selectedExpense.id}`;
      let body = {};
      let method = 'POST';
      
      switch (actionType) {
        case 'review':
          url += '/review';
          body = { notes: actionNotes || null };
          break;
        case 'authorize':
          url += '/authorize';
          body = { notes: actionNotes || null };
          break;
        case 'disburse':
          url += `/disburse${paymentRef ? `?payment_reference=${encodeURIComponent(paymentRef)}` : ''}`;
          body = { notes: actionNotes || null };
          break;
        case 'reject':
          url += '/reject';
          body = { notes: actionNotes };
          break;
        case 'send-back':
          url += '/send-back';
          body = { comment: actionNotes };
          break;
        case 'delete':
          url = `${API_URL}/api/expenses/admin/${selectedExpense.id}`;
          method = 'DELETE';
          body = null;
          break;
        default:
          return;
      }
      
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      
      if (response.ok) {
        const actionLabels = { 
          review: 'reviewed', 
          authorize: 'authorized', 
          disburse: 'disbursed',
          reject: 'rejected',
          'send-back': 'sent back',
          delete: 'deleted'
        };
        toast.success(`Expense ${actionLabels[actionType]} successfully`);
        setShowActionModal(false);
        setShowDetailModal(false);
        fetchExpenses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      console.error('Error performing action:', error);
      toast.error('Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const canPerformAction = (expense, action) => {
    // Check if user can perform the given action based on their badge AND the expense status
    const status = expense.status;
    
    switch (action) {
      case 'review':
        // Only reviewer (or admin) can review, and only when pending_review
        return status === 'pending_review' && (userBadges.includes('reviewer') || isAdmin);
      
      case 'disburse':
        // Only approver (or admin) can approve, and only when pending_disbursement
        return status === 'pending_disbursement' && (userBadges.includes('approver') || isAdmin);
      
      case 'authorize':
        // Only authorizer (or admin) can authorize, and only when pending_authorization
        return status === 'pending_authorization' && (userBadges.includes('authorizer') || isAdmin);
      
      case 'reject':
      case 'send-back':
        // Can reject/send-back only if user has the badge for the CURRENT stage
        if (status === 'pending_review') {
          return userBadges.includes('reviewer') || isAdmin;
        }
        if (status === 'pending_disbursement') {
          return userBadges.includes('approver') || isAdmin;
        }
        if (status === 'pending_authorization') {
          return userBadges.includes('authorizer') || isAdmin;
        }
        return false;
      
      case 'delete':
        // Admin can delete any expense including completed ones
        return isAdmin;
      
      default:
        return false;
    }
  };

  const getNextAction = (expense) => {
    // Get the primary action button based on expense status AND user's badge
    const status = expense.status;
    
    if (status === 'pending_review' && (userBadges.includes('reviewer') || isAdmin)) {
      return { action: 'review', label: 'Review', icon: FileSearch, btnClass: 'bg-blue-600 hover:bg-blue-700 text-white' };
    }
    if (status === 'pending_disbursement' && (userBadges.includes('approver') || isAdmin)) {
      return { action: 'disburse', label: 'Approve', icon: Banknote, btnClass: 'bg-green-600 hover:bg-green-700 text-white' };
    }
    if (status === 'pending_authorization' && (userBadges.includes('authorizer') || isAdmin)) {
      return { action: 'authorize', label: 'Authorize', icon: BadgeCheck, btnClass: 'bg-purple-600 hover:bg-purple-700 text-white' };
    }
    return null;
  };

  const filteredExpenses = expenses.filter(expense => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      expense.title?.toLowerCase().includes(search) ||
      expense.employee_name?.toLowerCase().includes(search) ||
      expense.request_id?.toLowerCase().includes(search)
    );
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 space-y-6"
    >
      {/* Header with Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-zinc-100">Expense Management</h1>
          <p className="text-gray-500 dark:text-zinc-400 text-sm">Manage expense requests and track spending</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-lg w-fit" data-testid="expense-tabs">
        <button
          onClick={() => setActiveTab('approvals')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'approvals'
              ? 'bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 shadow-sm'
              : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700'
          }`}
          data-testid="tab-approvals"
        >
          <span className="flex items-center gap-2">
            <Receipt className="h-4 w-4" /> Approvals
          </span>
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'analytics'
              ? 'bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 shadow-sm'
              : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700'
          }`}
          data-testid="tab-analytics"
        >
          <span className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Analytics
          </span>
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && <ExpenseAnalytics />}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && (
      <>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Receipt className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.total || 0}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileSearch className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.pending_review || 0}</p>
                <p className="text-xs text-gray-500">Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BadgeCheck className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.pending_authorization || 0}</p>
                <p className="text-xs text-gray-500">Auth</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Banknote className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.pending_disbursement || 0}</p>
                <p className="text-xs text-gray-500">Disburse</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Banknote className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.completed || 0}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.rejected || 0}</p>
                <p className="text-xs text-gray-500">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by Request ID, Employee, or Title..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending_review', 'pending_authorization', 'pending_disbursement', 'completed', 'rejected', 'returned'].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? 'bg-[#0F2E6E]' : ''}
                >
                  {status === 'all' ? 'All' : STATUS_LABELS[status]}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expense Logs Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Request Logs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading expense requests...</p>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No expense requests found</h3>
              <p className="text-gray-500 mt-1">No requests match your filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Request ID</TableHead>
                  <TableHead className="font-semibold">Employee</TableHead>
                  <TableHead className="font-semibold">Amount</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => (
                  <TableRow key={expense.id} className="hover:bg-gray-50">
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600 font-medium">
                        {expense.request_id || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{expense.employee_name}</p>
                          <p className="text-xs text-gray-500">{expense.employee_department}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {expense.currency} {formatCurrency(expense.total_amount)}
                        </p>
                        {expense.requires_authorization && (
                          <p className="text-xs text-purple-600 flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            High Value
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[expense.status]}>
                        {STATUS_LABELS[expense.status] || expense.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetail(expense)}
                        className="gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal with Actions, Workflow, and Details */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedExpense && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Request Details
                  </DialogTitle>
                  <Badge className={STATUS_COLORS[selectedExpense.status]}>
                    {STATUS_LABELS[selectedExpense.status]}
                  </Badge>
                </div>
                <DialogDescription className="flex items-center gap-2 pt-1">
                  <span className="font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-medium">
                    {selectedExpense.request_id || '-'}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Workflow Progress */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="text-sm font-semibold mb-3 block">Workflow Progress</Label>
                  <WorkflowProgress expense={selectedExpense} />
                </div>

                {/* Request Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Employee</p>
                    <p className="font-medium">{selectedExpense.employee_name}</p>
                    <p className="text-sm text-gray-500">{selectedExpense.employee_department}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="text-xl font-bold text-blue-600">
                      {selectedExpense.currency} {formatCurrency(selectedExpense.total_amount)}
                    </p>
                    {selectedExpense.requires_authorization && (
                      <p className="text-xs text-purple-600">Requires Authorization (≥ 1M)</p>
                    )}
                  </div>
                </div>

                {/* Title & Description */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Title</p>
                  <p className="font-medium">{selectedExpense.title}</p>
                  {selectedExpense.description && (
                    <p className="text-sm text-gray-600 mt-1">{selectedExpense.description}</p>
                  )}
                </div>

                {/* Purpose */}
                {selectedExpense.purpose && (
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <p className="text-xs font-medium text-blue-800 mb-1">Business Purpose</p>
                    <p className="text-sm text-blue-700">{selectedExpense.purpose}</p>
                  </div>
                )}

                {/* Items */}
                {selectedExpense.items?.length > 0 && (
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Expense Items</Label>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-xs">Description</TableHead>
                            <TableHead className="text-xs text-right">Qty</TableHead>
                            <TableHead className="text-xs text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedExpense.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm">{item.description}</TableCell>
                              <TableCell className="text-sm text-right">{item.quantity}</TableCell>
                              <TableCell className="text-sm text-right font-medium">
                                {selectedExpense.currency} {formatCurrency(item.amount * item.quantity)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Approval Trail */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Approval Trail</Label>
                  <div className="space-y-2">
                    {selectedExpense.submitted_at && (
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span>Submitted on {formatDate(selectedExpense.submitted_at)}</span>
                      </div>
                    )}
                    {selectedExpense.reviewed_by_name && (
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-sm">
                        <FileSearch className="h-4 w-4 text-blue-600" />
                        <span>Reviewed by <strong>{selectedExpense.reviewed_by_name}</strong></span>
                        <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.reviewed_at)}</span>
                      </div>
                    )}
                    {selectedExpense.authorized_by_name && (
                      <div className="flex items-center gap-2 p-2 bg-purple-50 rounded text-sm">
                        <BadgeCheck className="h-4 w-4 text-purple-600" />
                        <span>Authorized by <strong>{selectedExpense.authorized_by_name}</strong></span>
                        <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.authorized_at)}</span>
                      </div>
                    )}
                    {selectedExpense.disbursed_by_name && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span>Disbursed by <strong>{selectedExpense.disbursed_by_name}</strong></span>
                        <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.disbursed_at)}</span>
                      </div>
                    )}
                    {selectedExpense.returned_by_name && (
                      <div className="p-2 bg-orange-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <RotateCcw className="h-4 w-4 text-orange-600" />
                          <span>Returned by <strong>{selectedExpense.returned_by_name}</strong></span>
                          <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.returned_at)}</span>
                        </div>
                        <p className="mt-1 text-orange-700 pl-6">{selectedExpense.return_comment}</p>
                      </div>
                    )}
                    {selectedExpense.rejected_by_name && (
                      <div className="p-2 bg-red-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span>Rejected by <strong>{selectedExpense.rejected_by_name}</strong></span>
                          <span className="text-gray-500 ml-auto">{formatDate(selectedExpense.rejected_at)}</span>
                        </div>
                        <p className="mt-1 text-red-700 pl-6">{selectedExpense.rejection_reason}</p>
                      </div>
                    )}
                    {selectedExpense.payment_reference && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span>Payment Reference: <strong className="font-mono">{selectedExpense.payment_reference}</strong></span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <DialogFooter className="flex flex-wrap gap-2 pt-4 border-t">
                {(() => {
                  const nextAction = getNextAction(selectedExpense);
                  const hasActions = nextAction && canPerformAction(selectedExpense, nextAction.action);
                  
                  return (
                    <>
                      {hasActions && (
                        <>
                          <Button 
                            variant="outline"
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            onClick={() => openActionModal('send-back', selectedExpense)}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Send Back
                          </Button>
                          <Button 
                            variant="destructive" 
                            onClick={() => openActionModal('reject', selectedExpense)}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                          <Button 
                            className={nextAction.btnClass}
                            onClick={() => openActionModal(nextAction.action, selectedExpense)}
                          >
                            <nextAction.icon className="h-4 w-4 mr-2" />
                            {nextAction.label}
                          </Button>
                        </>
                      )}
                      {canPerformAction(selectedExpense, 'delete') && (
                        <Button 
                          variant="ghost"
                          className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => openActionModal('delete', selectedExpense)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      )}
                      <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                        Close
                      </Button>
                    </>
                  );
                })()}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === 'review' && <FileSearch className="h-5 w-5 text-blue-600" />}
              {actionType === 'authorize' && <BadgeCheck className="h-5 w-5 text-purple-600" />}
              {actionType === 'disburse' && <Banknote className="h-5 w-5 text-green-600" />}
              {actionType === 'reject' && <XCircle className="h-5 w-5 text-red-600" />}
              {actionType === 'send-back' && <RotateCcw className="h-5 w-5 text-orange-600" />}
              {actionType === 'delete' && <Trash2 className="h-5 w-5 text-red-600" />}
              {actionType === 'review' && 'Review Expense'}
              {actionType === 'authorize' && 'Authorize Expense (Final)'}
              {actionType === 'disburse' && 'Approve Expense'}
              {actionType === 'reject' && 'Reject Expense'}
              {actionType === 'send-back' && 'Send Back for Changes'}
              {actionType === 'delete' && 'Delete Expense'}
            </DialogTitle>
            <DialogDescription>
              {selectedExpense?.request_id && (
                <span className="font-mono text-blue-600">{selectedExpense.request_id}</span>
              )}
              {selectedExpense?.request_id && ' • '}
              {selectedExpense?.currency} {formatCurrency(selectedExpense?.total_amount || 0)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {actionType === 'review' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="font-medium text-blue-800">Next step:</p>
                <p className="text-blue-700">
                  Will proceed to Approver for approval
                  {selectedExpense?.requires_authorization && ' (high-value: will need final authorization after approval)'}
                </p>
              </div>
            )}
            
            {actionType === 'disburse' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                <p className="font-medium text-green-800">
                  {selectedExpense?.requires_authorization ? 'Approval - High Value Request' : 'Final Approval'}
                </p>
                <p className="text-green-700">
                  {selectedExpense?.requires_authorization 
                    ? 'After approval, this will proceed to Authorizer for final authorization.'
                    : 'This will complete the expense request.'}
                </p>
              </div>
            )}
            
            {actionType === 'authorize' && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                <p className="font-medium text-purple-800">Final Authorization</p>
                <p className="text-purple-700">
                  This is the final step for high-value expenses. Authorization will complete the request.
                </p>
              </div>
            )}
            
            {actionType === 'send-back' && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                <p className="font-medium text-orange-800">Send Back:</p>
                <p className="text-orange-700">
                  Request will be returned to submitter for changes.
                </p>
              </div>
            )}
            
            {actionType === 'delete' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <p className="font-medium text-red-800">Warning:</p>
                <p className="text-red-700">
                  This action cannot be undone. The expense request will be permanently deleted.
                </p>
              </div>
            )}
            
            {actionType !== 'delete' && (
              <div className="space-y-2">
                <Label>
                  {actionType === 'reject' ? 'Rejection Reason *' : 
                   actionType === 'send-back' ? 'Comment for Submitter *' : 
                   'Notes (optional)'}
                </Label>
                <Textarea
                  placeholder={
                    actionType === 'reject' ? 'Please provide a reason for rejection...' : 
                    actionType === 'send-back' ? 'Explain what changes are needed...' :
                    'Add any notes...'
                  }
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={3}
                />
              </div>
            )}
            
            {actionType === 'disburse' && (
              <div className="space-y-2">
                <Label>Payment Reference (optional)</Label>
                <Input
                  placeholder="e.g., TXN-123456"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction} 
              disabled={processing || (actionType === 'reject' && !actionNotes.trim()) || (actionType === 'send-back' && !actionNotes.trim())}
              className={
                actionType === 'review' ? 'bg-blue-600 hover:bg-blue-700' :
                actionType === 'authorize' ? 'bg-purple-600 hover:bg-purple-700' :
                actionType === 'disburse' ? 'bg-green-600 hover:bg-green-700' :
                actionType === 'send-back' ? 'bg-orange-600 hover:bg-orange-700' :
                'bg-red-600 hover:bg-red-700'
              }
            >
              {processing ? 'Processing...' : (
                actionType === 'review' ? 'Review & Forward' :
                actionType === 'authorize' ? 'Authorize & Complete' :
                actionType === 'disburse' ? 'Approve' :
                actionType === 'send-back' ? 'Send Back' :
                actionType === 'delete' ? 'Delete' :
                'Reject'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </motion.div>
  );
};

export default ExpenseManagement;
